class WmHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-wm-menu-toggle]');
    this.drawer = this.querySelector('[data-wm-mobile-drawer]');
    this.closeButton = this.querySelector('[data-wm-menu-close]');
    this.scrim = this.querySelector('[data-wm-menu-scrim]');
    this.cartLink = this.querySelector('.wm-header__cart');
    this.onEscape = this.onEscape.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.updateCartLabel = this.updateCartLabel.bind(this);

    this.toggle?.addEventListener('click', () => this.openMenu());
    this.closeButton?.addEventListener('click', () => this.closeMenu());
    this.scrim?.addEventListener('click', () => this.closeMenu());
    document.addEventListener('keydown', this.onEscape);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.cartObserver = new MutationObserver(this.updateCartLabel);
    if (this.cartLink) this.cartObserver.observe(this.cartLink, { childList: true, subtree: true, characterData: true });
    this.onScroll();
    this.updateCartLabel();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onEscape);
    window.removeEventListener('scroll', this.onScroll);
    this.cartObserver?.disconnect();
  }

  onEscape(event) {
    if (event.key === 'Escape' && this.hasAttribute('menu-open')) this.closeMenu();
  }

  onScroll() {
    this.classList.toggle('is-scrolled', window.scrollY > 16);
  }

  updateCartLabel() {
    if (!this.cartLink) return;
    const bubble = this.cartLink.querySelector('.cart-count-bubble');
    const countText = bubble?.querySelector('[aria-hidden="true"]')?.textContent || bubble?.textContent?.match(/\d+/)?.[0] || '0';
    const count = Number.parseInt(countText, 10) || 0;
    this.cartLink.setAttribute('aria-label', `Cart, ${count} ${count === 1 ? 'item' : 'items'}`);
  }

  openMenu() {
    this.setAttribute('menu-open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('wm-menu-open');
    this.closeButton?.focus();
  }

  closeMenu() {
    this.removeAttribute('menu-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('wm-menu-open');
    this.toggle?.focus();
  }
}

if (!customElements.get('wm-header')) customElements.define('wm-header', WmHeader);

class WmMotion {
  constructor() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.revealed = new WeakSet();
    this.parallaxItems = new Set();
    this.frameRequested = false;
    this.onScroll = this.onScroll.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);

    if ('IntersectionObserver' in window && !this.reducedMotion.matches) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
      );
    }

    this.init(document);
    document.documentElement.classList.add('wm-motion-ready');
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    this.onScroll();
  }

  init(scope) {
    const selector = [
      '.wm-section-head',
      '.wm-trust__item',
      '.wm-category-card',
      '.wm-look-card',
      '.wm-finder-choice',
      '.wm-how-to__step',
      '.wm-pro-bridge__panel',
      '.wm-spec-group',
      '.wm-product-card',
      '.wm-pathway-card',
      '.wm-wholesale__panel',
      '.wm-proof__item',
      '.wm-faq details',
      '.wm-pro-tier',
      '.wm-pro-process__steps li',
      '.wm-pro-ordering > *',
      '.wm-education__grid article',
      '.wm-education__accordions details',
      '.wm-education__resource',
      '.wm-studio-heritage__facts > div',
      '.wm-studio-heritage__pickup',
      '.wm-about__timeline li',
      '.wm-about__service-card',
      '.wm-about__principle-grid article',
      '.wm-about__visit-details > div',
      '.wm-about__faq-list details',
      '.wm-application__layout > *',
    ].join(',');

    scope.querySelectorAll(selector).forEach((element, index) => {
      if (this.revealed.has(element)) return;
      this.revealed.add(element);
      element.classList.add('wm-motion-item');
      element.style.setProperty('--wm-delay', `${(index % 4) * 65}ms`);

      if (!this.observer || this.reducedMotion.matches) {
        element.classList.add('is-visible');
      } else {
        this.observer.observe(element);
      }
    });

    scope.querySelectorAll('[data-wm-parallax]').forEach((element) => this.parallaxItems.add(element));
  }

  onSectionLoad(event) {
    this.init(event.target);
    this.onScroll();
  }

  onScroll() {
    if (this.frameRequested) return;
    this.frameRequested = true;
    window.requestAnimationFrame(() => {
      this.frameRequested = false;
      const allowParallax = !this.reducedMotion.matches && window.innerWidth >= 750;

      this.parallaxItems.forEach((element) => {
        if (!element.isConnected) {
          this.parallaxItems.delete(element);
          return;
        }

        if (!allowParallax) {
          element.style.setProperty('--wm-parallax-y', '0px');
          return;
        }

        const rect = element.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;
        const viewportCenter = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        const progress = Math.max(-1, Math.min(1, (elementCenter - viewportCenter) / window.innerHeight));
        element.style.setProperty('--wm-parallax-y', `${Math.round(progress * -18)}px`);
      });
    });
  }
}

const initWmMotion = () => {
  if (!window.wmMotion) window.wmMotion = new WmMotion();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWmMotion, { once: true });
} else {
  initWmMotion();
}
